import './PromoProfile.sass';

import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';

import { PromoProfileProps } from '$shared/components/PromoProfile/PromoProfile.interface';
import { classNames } from '$utils';
import { UnitPhoto, Verified, UnitName } from '$shared/components';
import { Button } from '$uikit';
import { useTranslator, useUserInfo, useVisibleDuration } from '$hooks';
import { adsActions } from '$store/ads';
import { followersActions, followersSelector } from '$store/followers';
import { CHAT, PROFILE, RESOLVE } from '$shared/constants/pages';

export const PromoProfile: FC<PromoProfileProps> = (props) => {
  const { cover, avatar, userId, adId } = props;
  const t = useTranslator();
  const user = useUserInfo(userId);
  const postRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { following } = useSelector(followersSelector);
  const isFollowed = following[userId];
  const router = useRouter();

  useVisibleDuration(postRef, () => {
    if (adId > 0) {
      dispatch(adsActions.sendView(adId));
    }
  });

  useEffect(() => {
    return () => {
      if (adId > 0) {
        dispatch(adsActions.sendView(adId));
      }
    };
  }, [adId]);

  const handleFollow = useCallback(
    (e) => {
      e.stopPropagation();
      dispatch(
        followersActions.followFromAds({
          toId: userId,
          adsId: adId,
        }),
      );
    },
    [userId, adId],
  );

  const handleChat = useCallback(
    (e) => {
      e.stopPropagation();
      router.navigate(CHAT, { peerId: userId });
    },
    [userId],
  );

  const handleClick = useCallback(() => {
    if (user.domain) {
      router.navigate(RESOLVE, { domain: user.domain });
    } else {
      router.navigate(PROFILE, { id: user.id });
    }
  }, [user]);

  return (
    <div className="PromoProfile__cover" ref={postRef} onClick={handleClick}>
      <div
        className={classNames('PromoProfile__cover_img', { fake: !cover })}
        style={{ backgroundImage: `url(${cover || avatar})` }}
      />

      <div className="PromoProfile__preview__info">
        {avatar ? (
          <div className="UnitPhoto medium">
            <img src={avatar} />
          </div>
        ) : (
          <UnitPhoto onlineIndicator={true} unit={user} size="medium" routeName={null} />
        )}

        <div className="PromoProfile__preview__info__text">
          <div className="PromoProfile__preview__info__name_wrap">
            <h1 className="PromoProfile__preview__info__name">
              <UnitName unit={user} />
            </h1>
            <Verified objectId={user.id} large />
          </div>
          <div className="PromoProfile__preview__info__subtitle">
            {user.followers} followers
          </div>
        </div>
        <div className="PromoProfile__preview__follow">
          {isFollowed ? (
            <Button size="small" onClick={handleChat} mode="secondary">
              {t('promo_post_message')}
            </Button>
          ) : (
            <Button size="small" onClick={handleFollow}>
              {t('promo_post_follow')}
            </Button>
          )}
        </div>
      </div>

      <div className="PromoProfile__preview__sponsored">{t('post_sponsored')}</div>
    </div>
  );
};
